import { atom } from 'jotai';
import {
  atomFamily,
  atomWithReducer,
  atomWithStorage,
  selectAtom,
} from 'jotai/utils';
import { isEqual } from 'lodash-es';
import type { MapRef } from 'react-map-gl';
import request from 'lib/request';
import { mutationAtomWithLoadable } from 'lib/atoms';
import { getMapDefaultConfig } from 'components/map/atoms';
import type { MapUserConfig } from 'components/map/types';
import type { BBox } from 'components/query-map/query-map';
import type {
  AppliedFilter,
  CashflowPeriod,
  RollupBin,
  RollupSort,
  ColorAction,
  ColorRule,
  DatasetName,
  DisplaySettings,
  FilterAction,
  MapScope,
  MasterDataView,
  ServerFilter,
  LayoutSectionKey,
  MapElements,
  ScopedMapLayers,
  ScopeView,
  SelectAllState,
  SpatialAction,
  SpatialFilterState,
  InventoryPackageOverride,
  InventoryPackageOverrideWire,
  TypeCurveOverride,
  TypeCurveOverrideWire,
} from './types';
import type { SlotOverrideRejection } from './schedule-dialog/types';
import type {
  AriesMdbKickoffResponse,
  AriesMdbExportConfig,
  AriesMdbExportDefaults,
  AriesRestoredRun,
} from 'components/aries-mdb-export/types';
import type { Chart, ChartAction } from './custom-chart/types';
import {
  EMPTY_INTEREST_MODEL_DELTA,
  type InterestModelDelta,
} from './interest-modeling-rollup';

/**
 * Default map config for eval3-new with the GLOBAL wells + development-areas
 * built-ins turned off: this page renders its own package-scoped Slot / Tract /
 * Unit layers (see `slot-layer` / `tract-layer` / `unit-layer`), so the global
 * layers would only double-draw where the package overlaps the viewport. Each
 * block's `options`
 * are preserved — only the top-level `checked` flag is flipped — so a user can
 * still toggle the globals back on from the layers panel.
 */
const eval3DefaultMapConfig: MapUserConfig = (() => {
  const base = getMapDefaultConfig();
  return {
    ...base,
    wellsV2: { ...base.wellsV2, checked: false },
    developmentAreas: { ...base.developmentAreas, checked: false },
  };
})();

/**
 * Persisted map user config for the eval3-new QueryMap instance.
 *
 * `atomWithStorage` so layer toggles survive reload, mirroring other map-using
 * pages (e.g. `pages/dca-scenarios-list`). `<Map>` expects a `MapUserConfigAtom`
 * (a primitive atom) for this prop. The default disables the global wells +
 * dev-areas layers; users with a previously stored config keep their own.
 */
export const mapConfigAtom = atomWithStorage<MapUserConfig>(
  'eval3-new:map-config',
  eval3DefaultMapConfig
);

/**
 * Visibility of the three package-scoped map layers (Slot / Tract / Unit). Shared
 * by the Slot/Tract/Unit layer components (mounted as QueryMap children in
 * `PackageMap`) and the toggle UI (`ScopedMapLayerToggles`, slotted into the
 * layers panel's "Scoped"
 * section). All on by default. In-memory only — not persisted.
 */
export const scopedMapLayersAtom = atom<ScopedMapLayers>({
  slots: true,
  tracts: true,
  units: true,
  // Type Curve LTD places (INS-10669) — on by default like the other scoped layers, so a
  // package's planned locations are visible without hunting for a toggle.
  ltd: true,
});

/**
 * The Type Curve Scenario the LTD places come from (INS-10669), or `null` for "not picked" —
 * which the BE resolves to Legacy, the same default the table, the catalogs and the calculation
 * use. Written by the Case-management scenario picker.
 *
 * An atom rather than a prop because five surfaces need it and they must agree: the slots table,
 * the filter catalogs, the executive summary, the map's `map_elements` visibility, and the LTD
 * tile URL. Reading it inside the map-visibility hooks also means every layer builds the SAME
 * `map_elements` query key, so they still dedupe to one fetch — prop-threading it through only
 * the LTD layer would have split that into two.
 */
export const typeCurveScenarioIdAtom = atom<number | null>(null);

/**
 * The Manage Type Curves modal's committed choices (INS-10669) — the same set that rides
 * the stored override log as `type_curve` entries, published for the read surfaces.
 *
 * The scenario above has an atom because five surfaces must agree on it. These need one for
 * exactly the same reason and one more besides: `slotOverrides` is deliberately kept OUT of
 * every read query key (an inline edit must not refetch — see `masterDataQueryKey`), which is
 * right for `individual` and `bulk` because they change a row's VALUES and are written
 * optimistically. A `type_curve` entry changes which rows EXIST, so there is nothing to write
 * optimistically and the table, the catalogs, the summary and the map all have to re-ask.
 * Without this atom they never learn the choice changed, and Done closes the modal over a
 * grid still showing the previous curve's places.
 *
 * Two writers, one meaning: SEEDED from the eval's committed log on load and eval switch
 * (useEval3Page), then rewritten only by `setTypeCurveOverrides` on the TC Manager's Done —
 * so the committed log and the previewed population cannot describe different curves. The
 * seed is load-bearing: without it the atom starts empty over an eval with committed
 * type_curve entries, and a Done that RESETS a row to default publishes [] over [] — no
 * query key changes and nothing repaints, though the log was correctly rewritten.
 */
export const typeCurveOverridesAtom = atom<readonly TypeCurveOverride[]>([]);

/**
 * The per-cell schedule-date-override rejections from the LAST Calculate attempt
 * (INS-12497). A `locked_reported` / `out_of_order` write is refused by the BE with a
 * `422 { errors: { slot_overrides: [...] } }`, which `useCalculate` publishes here
 * (cleared at the start of every attempt, set on a rejection). `SlotView` reads it to
 * mark the exact Permit/Spud/First-Production cell as rejected instead of painting the
 * optimistic green ✓ over a write the run never applied — the UI must not assert an
 * override the calculation ignored. Empty on success or any non-rejection failure.
 */
export const scheduleOverrideRejectionsAtom = atom<
  readonly SlotOverrideRejection[]
>([]);

/**
 * The spacing half of the TC Manager's committed choices (INS-10669) — the
 * `inventory_package` fold, published for exactly the reasons `typeCurveOverridesAtom`
 * is: a package choice changes which places EXIST, so the read surfaces must re-key on
 * it. Same two writers, same seed discipline.
 */
export const inventoryPackageOverridesAtom = atom<
  readonly InventoryPackageOverride[]
>([]);

/**
 * The session's PENDING manager-kind wire entries — `type_curve` AND `inventory_package`,
 * deltas AND tombstones, exactly as the TC Manager's Done appended them to the working log
 * (INS-11129 Phase 2). This is what the population read surfaces (filter catalogs, exec
 * summary, map elements, TC ledger) send in their `slot_overrides` body: the BE loads the
 * committed history via the calc id and folds these on top, so a revert-to-default (a
 * null-payload tombstone) actually DELETES the committed choice server-side. The folds
 * above cannot express that — they hold survivors only, and an overlay of survivors can
 * never remove a committed key. Published at the same sites as the folds (seed / Done /
 * run-success clear), all derived from the same working log, so they cannot drift.
 */
/**
 * The population mode the LAST master-data response reported, keyed by the calc it
 * described (INS-11129). The divergence banner's primary signal stays the CLIENT-side
 * fold/scenario comparison (usePopulationCalculationId — it flips without a round-trip);
 * this is the SERVER's authoritative echo, OR-ed in so any divergence source the client
 * comparison misses still surfaces. Keyed by calculationId so a stale verdict from a
 * previous eval never leaks into the next one's banner.
 */
export const serverPopulationModeAtom = atom<{
  calculationId: number | null;
  mode: 'frozen' | 'hybrid' | 'live';
} | null>(null);

export const pendingTypeCurveWireAtom = atom<
  readonly (TypeCurveOverrideWire | InventoryPackageOverrideWire)[]
>([]);

/**
 * In-memory ref to the active `<Map>` instance, for this feature to own one ref
 * source. `<Map>` defaults to its own internal ref atom, so the consumer must
 * pass this explicitly via the `refAtom` prop for `<Map>` to write here on mount.
 * Never persisted.
 */
export const mapRefAtom = atom<MapRef | null>(null);

/**
 * Layout-section visibility for the P2 surfaces (Executive Summary / Map / Slot
 * Allocations Table) plus the P5 `packageOverview` output section. ONE record
 * rather than a boolean-per-checkbox so the tri-state "Select all" can be derived
 * from reading every toggle at once (`selectAllStateAtom`), and so further
 * per-output-section keys (P6+) drop in without a refactor. All on by default.
 * In-memory only — not persisted.
 */
export const layoutSettingsAtom = atom<Record<LayoutSectionKey, boolean>>({
  executiveSummary: true,
  map: true,
  slotAllocationsTable: true,
  packageOverview: true,
  cashflowsByMonth: true,
  slotCount: true,
  productionVolumes: true,
  cashFlow: true,
  outputAggregation: true,
  chartsVisuals: true,
  interestModeling: true,
});

/**
 * Tri-state for the Layout Settings "Select all" checkbox, derived from
 * `layoutSettingsAtom` (derive, don't duplicate): `'checked'` when every section
 * is on, `'unchecked'` when all are off, `'indeterminate'` otherwise.
 */
export const selectAllStateAtom = atom<SelectAllState>((get) => {
  const values = Object.values(get(layoutSettingsAtom));
  if (values.every(Boolean)) {
    return 'checked';
  }
  return values.some(Boolean) ? 'indeterminate' : 'unchecked';
});

/**
 * Reducer for `datasetFiltersAtom`. `set` swaps the whole list (the panel's
 * merge-by-attribute result, applied atomically as one re-render); `add` appends;
 * `update` replaces the entry with the matching `id`; `remove` drops it; `clear`
 * empties. Jotai types the action as optional, so bail to the current state when
 * it's absent (lets the discriminated union narrow in the switch).
 *
 * @param {AppliedFilter[]} state - The current applied filters.
 * @param {FilterAction} [action] - The transition to apply.
 * @return {AppliedFilter[]} The next applied filters.
 */
const filterReducer = (
  state: AppliedFilter[],
  action?: FilterAction
): AppliedFilter[] => {
  if (!action) {
    return state;
  }
  switch (action.type) {
    case 'set':
      return action.filters;
    case 'add':
      return [...state, action.filter];
    case 'update':
      return state.map((filter) => {
        return filter.id === action.filter.id ? action.filter : filter;
      });
    case 'remove':
      return state.filter((filter) => filter.id !== action.id);
    case 'clear':
      return [];
    default:
      return state;
  }
};

/**
 * The applied dataset filters — ONE reducer list across wells / units / assets
 * (they all funnel into the same table + summary + map requests, so there's no
 * surface to isolate per dataset). Each entry carries its `dataset` + `catalog`.
 * Written by `PageControlsPanel` (`set`, the merged apply result) and the filter
 * cards' × (`remove`); read by `serverFiltersAtom`. In-memory only.
 */
export const datasetFiltersAtom = atomWithReducer<
  AppliedFilter[],
  FilterAction
>([], filterReducer);

/**
 * Derived server filters for the data endpoints (table + summary + map all read
 * this; the two table/summary endpoints ignore `catalog`). Maps each
 * `AppliedFilter` to a `ServerFilter`, tagging `catalog` = its dataset and
 * carrying only the populated half (selected values OR range). The mapping is
 * inlined here rather than imported from `utils.tsx` because `utils.tsx` imports
 * `locatedRowAtom` from this module — pulling a util back in would make atoms ↔
 * utils a cycle.
 */
export const serverFiltersAtom = atom<ServerFilter[]>((get) =>
  get(datasetFiltersAtom).map((filter) => ({
    attribute: filter.attribute,
    catalog: filter.dataset,
    ...(filter.selectedValues && { selectedValues: filter.selectedValues }),
    ...(filter.range && { range: filter.range }),
  }))
);

/**
 * Asset-picker scope flag: `true` makes every data read that carries the
 * page filter state (master_data / map_elements / filter_options) also send
 * `interestModelableOnly: true`, narrowing results server-side to "modelable" tracts
 * (instrument type OGL/UMI). Defaults `false` — the page never sends the flag.
 * The Add-Properties picker hydrates it `true` inside its OWN fresh Jotai
 * Provider, so the flag (like the picker's filters/scope) never leaks into the
 * page's atoms; the hooks read it the same way they read `serverFiltersAtom`.
 * In-memory only.
 */
export const interestModelableOnlyAtom = atom(false);

/**
 * The live interest-modeling contribution to the package Target / Max value — written
 * by the always-mounted `InterestModelingSection` from its working-config preview
 * (carry net-to-us + each elected UMI outcome) and read by the `ExecutiveSummary` for
 * its contribution sentence. Bridges the two sibling sections (they share this page's
 * Jotai provider but not a parent that holds the preview), so the summary tracks the
 * live edits rather than the last committed run. Defaults to the zero contribution.
 */
export const interestModelDeltaAtom = atom<InterestModelDelta>(
  EMPTY_INTEREST_MODEL_DELTA
);

// ─── Charts & Visuals (INS-9994) ───

/**
 * Reducer for `chartsAtom`, mirroring `filterReducer`. `set` swaps the whole list (the
 * "Show All" toggle result, applied atomically as one re-render); `add` appends;
 * `update` replaces the entry with the matching `id`; `setVisible` sets one chart's
 * `visible` to the row checkbox's explicit next value; `remove` drops it; `clear`
 * empties. The single-chart transitions match on `instanceId` (a chart's stable identity).
 * Jotai types the action as optional, so bail to the current state when
 * it's absent (lets the discriminated union narrow in the switch).
 *
 * @param {Chart[]} state - The current charts.
 * @param {ChartAction} [action] - The transition to apply.
 * @return {Chart[]} The next charts.
 */
const chartsReducer = (state: Chart[], action?: ChartAction): Chart[] => {
  if (!action) {
    return state;
  }
  switch (action.type) {
    case 'set':
      return action.charts;
    case 'add':
      return [...state, action.chart];
    case 'update':
      return state.map((chart) => {
        return chart.instanceId === action.chart.instanceId
          ? action.chart
          : chart;
      });
    case 'setVisible':
      return state.map((chart) => {
        return chart.instanceId === action.instanceId
          ? { ...chart, visible: action.visible }
          : chart;
      });
    case 'remove':
      return state.filter((chart) => chart.instanceId !== action.instanceId);
    case 'clear':
      return [];
    default:
      return state;
  }
};

/**
 * The charts configured in the Charts & Visuals section (mock §4d) — ONE reducer list
 * mirroring `datasetFiltersAtom`. Written by `ChartsVisualsSection` (`add` / `update`
 * from the add-chart modal, `setVisible` from a row checkbox, `set` from "Show All",
 * `remove` from a row's delete); read by the work-area chart grid (deferred). In-memory
 * only today — the backend seed/save (`useCharts` / `useSaveCharts`) is the one net-new
 * piece still to land.
 */
export const chartsAtom = atomWithReducer<Chart[], ChartAction>(
  [],
  chartsReducer
);

/**
 * Tri-state for the Charts & Visuals "Show All" checkbox, derived from `chartsAtom`
 * (derive, don't duplicate) and mirroring `selectAllStateAtom`: `'checked'` when every
 * chart is visible, `'unchecked'` when none are (or the list is empty), else
 * `'indeterminate'`.
 */
export const chartsShowAllStateAtom = atom<SelectAllState>((get) => {
  const charts = get(chartsAtom);
  if (charts.length === 0) {
    return 'unchecked';
  }
  const flags = charts.map((chart) => chart.visible);
  if (flags.every(Boolean)) {
    return 'checked';
  }
  return flags.some(Boolean) ? 'indeterminate' : 'unchecked';
});

/**
 * Whether the Charts & Visuals section is currently RENDERED on the page — true iff its
 * Layout Settings toggle is on AND ≥1 chart is visible (mirrors `ChartsWorkArea`, which
 * is conditionally mounted on `layout.chartsVisuals` and renders `null` when no chart is
 * visible). Drives the content-export modal's Charts & Visuals checkbox: SciChart charts
 * are captured WYSIWYG (their live on-screen surface), so the checkbox is DISABLED when
 * nothing is rendered to capture. `derive, don't duplicate` — no separate flag.
 */
export const isChartsVisualsRenderedAtom = atom(
  (get) =>
    get(layoutSettingsAtom).chartsVisuals &&
    get(chartsAtom).some((chart) => chart.visible)
);

/**
 * Whether the Master Data map is currently RENDERED — true iff its Layout Settings toggle is
 * on (the map region is `display:none` when off, so its Mapbox canvas is 0-size). Drives the
 * content-export modal's Map checkbox: the map image is captured WYSIWYG from the live canvas,
 * so a hidden map can't be captured — the checkbox is DISABLED when the map isn't shown.
 */
export const isMapRenderedAtom = atom((get) => get(layoutSettingsAtom).map);

/** Default chart-grid dimensions (rows × columns) — the mock's 2×2. */
export const DEFAULT_CHART_GRID = { rows: 2, columns: 2 } as const;

/**
 * The Charts & Visuals work-area grid dimensions (mock §Charts). Lifted out of
 * `ChartsWorkArea`'s local state so it can be seeded from the persisted `chart_config` on
 * load and observed by the chart-config autosave. Written by the Rows/Columns inputs; read
 * by the grid and the serializer. Per-eval, in-memory (rehydrated from the backend on load).
 */
export const chartGridAtom = atom<{ rows: number; columns: number }>({
  ...DEFAULT_CHART_GRID,
});

/**
 * Reducer for `spatialFilterAtom`. `setMode` switches the Global Map Filter mode,
 * clearing any captured polygon on a real mode change — the geometry is
 * mode-specific, so Package Area then carries no scope and a stale AOI never leaks
 * into Visible Extent or back (the new mode recaptures: Extent immediately, AOI on
 * draw, Package Area never). A same-mode dispatch is a no-op so a re-render can't
 * wipe a freshly captured polygon. `setPolygon` stores the AOI / visible-extent
 * geometry. Jotai types the action as optional, so bail to the current state when
 * it's absent (lets the discriminated union narrow in the switch).
 *
 * @param {SpatialFilterState} state - The current spatial scope.
 * @param {SpatialAction} [action] - The transition to apply.
 * @return {SpatialFilterState} The next spatial scope.
 */
const spatialReducer = (
  state: SpatialFilterState,
  action?: SpatialAction
): SpatialFilterState => {
  if (!action) {
    return state;
  }
  switch (action.type) {
    case 'setMode':
      return action.mode === state.mode
        ? state
        : { mode: action.mode, polygon: null };
    case 'setPolygon':
      // Deep-equal skip: a geometrically-unchanged emit (e.g. a settled pan that
      // lands on the same viewport) keeps the current reference, so the derived
      // `mapScopeAtom` identity is stable and the reference-compare consumers
      // (the page-1 reset, the pin signal) don't churn on a no-op.
      return isEqual(state.polygon, action.polygon)
        ? state
        : { ...state, polygon: action.polygon };
    default:
      return state;
  }
};

/**
 * The spatial-scope half of the page filter state (the Global Map Filter). Written
 * by `SpatialFilterControls` (mode) and `PackageMap`'s query bundle (polygon); read
 * by `mapScopeAtom` (the derived endpoint payload) and `activeFilterCountAtom`.
 * Defaults to Package Area (whole package, no spatial narrowing). In-memory only.
 */
export const spatialFilterAtom = atomWithReducer<
  SpatialFilterState,
  SpatialAction
>({ mode: 'packageArea', polygon: null }, spatialReducer);

/**
 * Derived `MapScope` payload for the data endpoints (table + summary + map), or
 * `undefined` when there's no spatial narrowing (Package Area / no polygon yet).
 * Both spatial modes resolve to one `GeoJSON.Polygon`, so the wire shape is
 * uniform. The two table/summary endpoints and `map_elements` all take this.
 */
export const mapScopeAtom = atom<MapScope | undefined>((get) => {
  const { polygon } = get(spatialFilterAtom);
  return polygon ? { type: 'polygon', polygonGeoJson: polygon } : undefined;
});

/**
 * Whether the drawn AOI is in vertex-editing (`direct_select`) on the map. A controlled,
 * two-way mirror: the Global Map Filter's applied-AOI card sets it `true` (its "Edit"
 * control → `QueryMap`'s single-polygon `editing`, which puts the polygon into
 * `direct_select`), and the draw control writes it back (`onEditingChange`) from the map's
 * actual mode — so it also reflects native click-to-edit and clears to `false` when the
 * user clicks away. The AOI geometry itself stays the single source of truth in
 * `spatialFilterAtom`; this is only the edit-mode flag. In-memory only.
 */
export const aoiEditingAtom = atom(false);

/**
 * Count of active filters — drives the Executive Summary's "Filtered view" banner
 * (`isFiltered = activeFilterCount > 0`). Counts the dataset filters plus the
 * spatial scope (a set polygon = one active filter; Package Area / no polygon yet
 * adds nothing).
 */
export const activeFilterCountAtom = atom(
  (get) =>
    get(datasetFiltersAtom).length + (get(spatialFilterAtom).polygon ? 1 : 0)
);

/**
 * Reducer for `colorRulesAtom(dataset)`. `set` swaps the whole list (the panel's
 * merge-by-attribute apply result, applied atomically as one re-render); `add`
 * appends; `update` replaces the entry with the matching `id`; `remove` drops it;
 * `setEnabled` sets one rule's `enabled` to the card's explicit next value (the
 * card is a controlled checkbox, so it passes the value rather than a blind flip —
 * two events can't desync it); `clear` empties. Jotai types the action as optional,
 * so bail to the current state when it's absent (lets the union narrow in the
 * switch).
 *
 * @param {ColorRule[]} state - The current color rules for one dataset.
 * @param {ColorAction} [action] - The transition to apply.
 * @return {ColorRule[]} The next color rules.
 */
const colorReducer = (
  state: ColorRule[],
  action?: ColorAction
): ColorRule[] => {
  if (!action) {
    return state;
  }
  switch (action.type) {
    case 'set':
      return action.rules;
    case 'add':
      return [...state, action.rule];
    case 'update':
      return state.map((rule) => {
        return rule.id === action.rule.id ? action.rule : rule;
      });
    case 'remove':
      return state.filter((rule) => rule.id !== action.id);
    case 'setEnabled':
      return state.map((rule) => {
        return rule.id === action.id
          ? { ...rule, enabled: action.enabled }
          : rule;
      });
    // Recolor one value / the range in place (the card swatches): merge the single
    // field against current state by id, so a quick second pick can't be clobbered
    // by a stale whole-rule snapshot.
    case 'setValueColor':
      return state.map((rule) => {
        return rule.id === action.id
          ? {
              ...rule,
              valueColors: {
                ...rule.valueColors,
                [action.value]: action.color,
              },
            }
          : rule;
      });
    case 'setRangeColor':
      return state.map((rule) => {
        return rule.id === action.id
          ? { ...rule, rangeColor: action.color }
          : rule;
      });
    case 'clear':
      return [];
    default:
      return state;
  }
};

/**
 * Per-dataset color rules — an `atomFamily` keyed by `DatasetName` so a wells color
 * change re-renders ONLY the Slot layer's paint (the Tract / Unit layers read their
 * own dataset's atom; design.md re-render guard, mirrors type-curves). Each
 * dataset's list is the rules shown in that accordion's Color by Attribute section,
 * in listing order (precedence: last-listed wins on a shared feature). Written by
 * `PageControlsPanel` (`set` — the merge result) and the color cards (`setEnabled` /
 * `remove`); read by the dataset's layer (paint) and `useColorElements`. In-memory
 * only — not persisted. (One rule per attribute; Duplicate is deferred.)
 */
export const colorRulesAtom = atomFamily((_dataset: DatasetName) =>
  atomWithReducer<ColorRule[], ColorAction>([], colorReducer)
);

/**
 * The change signal that clears the pinned "show on map" row (FR#4 + the
 * Visible-Extent carve-out). `usePinResetOnFilterChange` subscribes to THIS rather
 * than the raw filter / spatial atoms, so the always-mounted root re-renders (and
 * the pin clears) only on a genuine pin-clearing event — NOT on every Visible-Extent
 * pan. Its reference changes on: a dataset-filter change, a spatial MODE change, or
 * — in AOI mode only — a polygon commit. A polygon change in Extent mode (a pan) is
 * deliberately ignored: clearing the pin there would loop (pin → fitToBounds pans the
 * map → moveend → new viewport → clear → …) and churn the pin on every nudge. The
 * custom equality suppresses notification for those pans. `serverFiltersAtom` /
 * `spatialFilterAtom` are derived/reducer atoms with stable references between real
 * changes, so the identity compares below are sufficient.
 */
export const pinResetSignalAtom = selectAtom(
  atom((get) => ({
    filters: get(serverFiltersAtom),
    spatial: get(spatialFilterAtom),
  })),
  // Identity slice — the change-suppression lives entirely in the equality fn below.
  (combined) => combined,
  (a, b) =>
    a.filters === b.filters &&
    a.spatial.mode === b.spatial.mode &&
    // In AOI mode the drawn polygon is part of the signal; in Package Area / Extent
    // it isn't (Extent pans must not clear the pin).
    (b.spatial.mode !== 'aoi' || a.spatial.polygon === b.spatial.polygon)
);

/**
 * The geometry one expanded-row child contributes to the map highlight, derived
 * on the FE from the child's scalar fields (the BE emits `mapElements` only at the
 * parent grain). `id` matches the child subtable's `getRowId`, so a checkbox toggle
 * maps straight to a `ChildHighlight`. `uwis` feed the Slot layer's `uwi` filter;
 * `names` feed the Tract / Unit layers' `name` filter.
 */
export interface ChildHighlight {
  id: string;
  uwis: string[];
  names: string[];
  /**
   * Type Curve LTD place ids this child covers (INS-10669). Optional because a child is a
   * tract or subtract of its parent slot and carries no places of its own — only the parent
   * row does. Declared for shape parity so the pin fold reads it uniformly.
   */
  slotIds?: string[];
}

/**
 * One pinned "show on map" row. Carries everything the map needs so pin + checkbox
 * are pure operations on this one shared object:
 * - `mapElements` — the BE's full related-geometry bundle (the all-checked baseline).
 * - `childHighlights` — per-child geometry, so unchecking a child can subtract it.
 * - `excludedChildIds` — the currently-unchecked children (the "exceptions"); empty
 *   = all children included (the bulk-pin default).
 * - `bbox` — optional camera framing (the BE merges it only when geometry is found;
 *   a geometry-less row still highlights by name, it just isn't framed).
 */
export interface LocatedRow {
  id: string;
  mapElements: MapElements;
  childHighlights: ChildHighlight[];
  excludedChildIds: string[];
  bbox?: BBox;
}

/**
 * Transitions for `locatedRowAtom`. The pin icon and the subtable checkboxes are
 * two composable operations over this one shared selection (order-independent):
 * - `pin` — BULK: pin the row with every child included (`excludedChildIds: []` —
 *   show everything related). The locate icon dispatches this on a fresh pin.
 * - `setExclusions` — LOCAL / auto-pin: set the pinned row to `row` with the given
 *   exclusions. Swaps the pinned row in when `row` differs from the current pin (so
 *   toggling a checkbox on an unpinned row auto-pins it); updates in place when it
 *   matches. The subtable checkboxes dispatch this.
 * - `clear` — unpin (the locate icon when already pinned, or a view change).
 */
export type LocatedRowAction =
  | { type: 'pin'; row: LocatedRow }
  | { type: 'setExclusions'; row: LocatedRow; excludedChildIds: string[] }
  | { type: 'clear' };

/**
 * The pinned "show on map" row(s). Written by the locate-column pin cells (`pin` /
 * `clear`) and the expanded-row child checkboxes (`setExclusions`), and read by
 * `PackageMap` to frame the map (each bbox feeds `QueryMap`'s `fitToBounds`) and by
 * `mapHighlightAtom` to scope the layers. A reducer so the transitions are explicit
 * and centralized. Holds a collection to support multi-select later; today it only
 * ever holds one entry. In-memory only — not persisted.
 */
export const locatedRowAtom = atomWithReducer<LocatedRow[], LocatedRowAction>(
  [],
  (state, action) => {
    // jotai types the reducer's action as optional; bail to the current state
    // when it's absent so the discriminated union narrows in the switch below.
    if (!action) {
      return state;
    }
    switch (action.type) {
      case 'pin':
        // Bulk: pin this row with all children included.
        return [{ ...action.row, excludedChildIds: [] }];
      case 'setExclusions':
        // Local/auto-pin: this row becomes the (single) pinned row with the given
        // exclusions — a swap when it's a different row, an update when it's the same.
        return [{ ...action.row, excludedChildIds: action.excludedChildIds }];
      case 'clear':
        return [];
      default:
        return state;
    }
  }
);

/**
 * The active map-highlight sets, or `null` when nothing is pinned. `null` is the
 * "show everything" signal the scoped layers read to drop their membership
 * filter; a non-null value (even with an empty array for one layer) means "filter
 * — show only these". Unions + dedupes across all pinned rows so it already
 * supports multi-select; today the pin only ever sets one row (via `pin` /
 * `setExclusions`).
 */
export type MapHighlight = MapElements | null;

/**
 * Derived from `locatedRowAtom`: the geometry the scoped layers highlight, or
 * `null` when nothing is pinned. Per pinned row the highlight is its **own**
 * geometry (`mapElements` minus everything its children contribute — the pinned
 * entity itself, which always shows) **unioned with the geometry of its
 * still-checked children**. So:
 *  - all children checked (`excludedChildIds` empty) → exactly `mapElements`;
 *  - unchecking a child drops its ids unless `own` or another checked child still
 *    contributes them (a shared tract/unit polygon survives one well being hidden);
 *  - unchecking every child → just the pinned entity's own geometry.
 * Read by the Slot/Tract/Unit layers to build their Mapbox highlight filters.
 */
export const mapHighlightAtom = atom<MapHighlight>((get) => {
  const rows = get(locatedRowAtom);
  if (rows.length === 0) {
    return null;
  }
  const uwis = new Set<string>();
  // Type Curve LTD places (INS-10669): a place has no UWI and its unit name would light up
  // every sibling place, so its own composite id is the only identifier that can select it.
  const slotIds = new Set<string>();
  const names = new Set<string>();
  rows.forEach(({ mapElements, childHighlights, excludedChildIds }) => {
    const excluded = new Set(excludedChildIds);
    // Everything the children cover — used to carve out the row's "own" geometry
    // (mapElements minus all children) so the pinned entity always shows.
    const childUwis = new Set(childHighlights.flatMap((child) => child.uwis));
    const childNames = new Set(childHighlights.flatMap((child) => child.names));
    const childSlotIds = new Set(
      childHighlights.flatMap((child) => child.slotIds ?? [])
    );
    mapElements.uwis.forEach((uwi) => {
      if (!childUwis.has(uwi)) {
        uwis.add(uwi);
      }
    });
    mapElements.slotIds.forEach((slotId) => {
      if (!childSlotIds.has(slotId)) {
        slotIds.add(slotId);
      }
    });
    mapElements.names.forEach((name) => {
      if (!childNames.has(name)) {
        names.add(name);
      }
    });
    // Re-add every still-checked child's geometry.
    childHighlights.forEach((child) => {
      if (!excluded.has(child.id)) {
        child.uwis.forEach((uwi) => uwis.add(uwi));
        (child.slotIds ?? []).forEach((slotId: string) => slotIds.add(slotId));
        child.names.forEach((name) => names.add(name));
      }
    });
  });
  return { uwis: [...uwis], slotIds: [...slotIds], names: [...names] };
});

// ─── P3 · Workspace & Eval Navigation ───

// NOTE: which center surface is showing (a workspace's management/browse view vs. the
// eval work area) is NOT an atom — it's the route-derived `workspaceId` on the page
// identity (`useEval3Page`), so the URL is the single source of truth for both the open
// folder AND the active eval (which sits under it). See `context.tsx`.

/**
 * Scope dropdown value. Default `'all'` — diverges from the mock's `'my'` to avoid an
 * empty default for a user who created no workspaces (Mine is `createdBy`-only). Written
 * and read by `EvalWorkspace`. In-memory only — not persisted.
 */
export const scopeViewAtom = atom<ScopeView>('all');

// ─── P5 · Calculate, Eval Settings & Package Overview ───

/**
 * Whether the right Eval Settings panel is open. Open by default (the panel snaps in
 * with no initial animation; closing/reopening animates the width 480 ↔ 0). Owned by
 * `Eval3NewContent` (it toggles open/close); the panel itself reads no atoms — it
 * requests close via its `onRequestClose` prop, not by writing here. In-memory only.
 */
export const evalSettingsPanelOpenAtom = atom(true);

/**
 * Whether the left pane (Evals tree + Page Controls) is open. Open by default — like the
 * right Eval Settings panel, it snaps in with no initial animation and animates its width
 * (380 ↔ 0) on close / reopen. Toggled by the `LeftPaneToggle` in the center title bar and
 * read by `LeftPane`. In-memory only — not persisted.
 */
export const leftPaneOpenAtom = atom(true);

/**
 * Which activity feed to show — `'package'` for the package-level feed (workspace/eval
 * creation), `'calculation'` for the selected calculation's feed (runs, exports, comments).
 * Written by the two activity buttons (left pane header + center header) and read by the
 * `ActivityFeedPanel` in `Eval3NewContent`.
 */
export type ActivityFeedMode = 'package' | 'calculation';
export const activityFeedModeAtom = atom<ActivityFeedMode>('package');

// NOTE: the client-only from-scratch draft (Model A) is NOT an atom — it's the
// route-derived `isDraft` on the page identity (`useEval3Page`), via the `eval/new`
// sentinel segment. Its workspace is the route `workspaceId`. See `context.tsx`.

/**
 * Output display preferences — one grouped object atom (matching the feature's
 * grouped-atom convention, e.g. `datasetFiltersAtom` / `spatialFilterAtom`), sliced
 * by consumers with `selectAtom`: `.aggregation` → `useEvalResult` (the one
 * `eval_result` time input), `.dateRange` → `resolveDateRange`, `.measurementType`
 * → no P5 reader (the controls are inert over Package Overview but shipped wired for
 * the deferred time-series sections). Written per-field by `OutputDisplayControls`.
 * Mock defaults: monthly / net / relative (2 before, 10 after). In-memory only.
 */
export const displaySettingsAtom = atom<DisplaySettings>({
  aggregation: 'monthly',
  measurementType: 'net',
  dateRange: {
    mode: 'relative',
    relative: { pre: 2, post: 10 },
    absolute: { min: null, max: null },
  },
});

/**
 * The aggregation slice of `displaySettingsAtom` — the one `eval_result` time input
 * (read by `PackageOverviewSection`). A `selectAtom` so the section re-renders only
 * when `aggregation` changes, not when `measurementType` / `dateRange` do (those have
 * no `eval_result` reader). Mirrors the design's "consumers slice with `selectAtom`".
 */
export const displayAggregationAtom = selectAtom(
  displaySettingsAtom,
  (settings) => settings.aggregation
);

/**
 * The measurement-type slice of `displaySettingsAtom` (`'net'` | `'gross'`) — read by
 * `ProductionVolumesSection` to pick which series the chart plots. A `selectAtom` so the
 * section re-renders only when `measurementType` changes, not when `aggregation` /
 * `dateRange` do. Mirrors `displayAggregationAtom`.
 */
export const displayMeasurementTypeAtom = selectAtom(
  displaySettingsAtom,
  (settings) => settings.measurementType
);

/**
 * The date-range slice of `displaySettingsAtom` — read by `ProductionVolumesSection`
 * (via `resolveDateRange`) to window the chart. A `selectAtom` so the section re-renders
 * only when `dateRange` changes, not when `aggregation` / `measurementType` do. Mirrors
 * `displayAggregationAtom`.
 */
export const displayDateRangeAtom = selectAtom(
  displaySettingsAtom,
  (settings) => settings.dateRange
);

// ─── P6 · Output Aggregation (ValueRollup) — view-state ───
//
// The rollup + chart view selections. Module-scoped so they PERSIST across an
// eval-switch and a section remount (FR#15), and IN-MEMORY only — a full page
// reload reseeds from `config.defaults` (consistent with the rest of eval3-new).
// The `null`-holding atoms mean "unseeded": the section derives the effective
// value as `atom ?? options.defaults.*`, so no seeding effect is needed and a
// reload falls back to defaults.
//
// Pagination is DELIBERATELY NOT an atom — `page` / `pageSize` are section-owned
// state that reset to page 1 on any control change (the `useMasterDataView`
// pattern).

// ── Table view-state ──

/** Table Group-by dimensions. `null` → `options.defaults.dimensions`. */
export const rollupDimensionsAtom = atom<string[] | null>(null);

/** Table single-column sort. `null` → unsorted (the BE's default order). */
export const rollupSortAtom = atom<RollupSort | null>(null);

/**
 * The IMMEDIATE search value — the controlled `globalFilter` fed to DataTable so
 * the input stays responsive on every keystroke. The debounced
 * `rollupSearchQueryAtom` (below) is what the query actually keys on.
 */
export const rollupSearchAtom = atom<string>('');

/**
 * The DEBOUNCED search value `useValueRollup` keys on — written from the search
 * handler through a `lodash.debounce` (the debounce lives outside DataTable). So
 * the input is instant while the fetch is debounced.
 */
export const rollupSearchQueryAtom = atom<string>('');

/** Table cashflow-period window. `null` → no window (unscaled values). */
export const rollupCashflowPeriodAtom = atom<CashflowPeriod | null>(null);

/**
 * The single active table bin (numeric range OR temporal period), or `null` for "None".
 * ONE atom ⇒ the two categories are mutually exclusive by construction: selecting one
 * replaces the other, so there is no clearing logic. Not seeded from `options.defaults`
 * (there is no default bin); a full reload falls back to "None".
 */
export const rollupBinAtom = atom<RollupBin | null>(null);

/**
 * The BE export row keys of each master-data table's shown columns, in on-screen order, from
 * the mounted view's table (its `onVisibleColumnsChange`, via `useMasterDataVisibleColumns`).
 * Drives the content export's "Current View" preset — read imperatively at export-click.
 * `null` for a view the user hasn't opened this session (→ the export falls back to all
 * columns for it). Module-scoped so a value survives view switches (only one view mounts at
 * a time).
 */
export const masterDataVisibleColumnsAtom = atom<
  Record<MasterDataView, string[] | null>
>({
  assets: null,
  slots: null,
  allocations: null,
});

/**
 * The Cashflows-by-Month table's shown column ids, in on-screen order, from its table's
 * `onVisibleColumnsChange`. Drives the content export's "Current View" preset for the cashflows
 * section (read imperatively at export-click). `null` until the table has reported (→ the
 * export falls back to all columns). Distinct from `masterDataVisibleColumnsAtom` because the
 * vocabulary differs (warehouse column ids, which the BE underscores).
 */
export const cashflowsVisibleColumnsAtom = atom<string[] | null>(null);

/**
 * Whether the % of Package badges show. The per-row `pctOfPackage` fractions
 * already ride every row, so this is a pure show/hide of the badges (no refetch).
 */
export const isPercentOfPackageShownAtom = atom<boolean>(false);

// ── Chart view-state (independent of the table, per FR#12 / FR#13) ──

/** Chart Group-by dimensions. `null` → `options.defaults.dimensions`. */
export const chartDimensionsAtom = atom<string[] | null>(null);

/** Chart metric. `null` → `options.defaults.metric`. */
export const chartMetricAtom = atom<string | null>(null);

/** Chart cashflow-period window (its own value). `null` → no window. */
export const chartCashflowPeriodAtom = atom<CashflowPeriod | null>(null);

// ---------------------------------------------------------------------------
// Aries MDB Export (INS-10551 / INS-10553 / INS-10555)
//
// Ported from the legacy `eval-3-workspace` panel — same endpoints + payload
// contracts. Backs the "Actions → Export Aries MDB" flow on the eval3-new
// center header (config modal + live progress + per-calc restore).
// ---------------------------------------------------------------------------

// GET /api/jobs/processes/latest_for_calculation?calculation_id=xxx — fetch the
// selected calc's latest Aries MDB run to rehydrate on tile-switch / page load.
// Resolves to the run payload, or null when the calc has no run (the endpoint
// 404s → `request.get` returns `{ error }` → treated as "nothing to restore",
// so the prior calc's run state is cleared and nothing bleeds across calcs).
export const [fetchAriesLatestForCalculationBaseAtom] =
  mutationAtomWithLoadable<AriesRestoredRun | null, [number]>(
    async (_get, _set, calculationId) => {
      const response = await request.get<{ data?: AriesRestoredRun }>(
        `/api/jobs/processes/latest_for_calculation?calculation_id=${calculationId}`
      );
      if ('error' in response) return null;
      return response.data ?? null;
    }
  );

// GET /evaluations/calculations/:calculation_id/downloads/aries_mdb_export/new
// — Modal defaults (INS-10553). Returns the auto-derived config the config
// modal renders (D4/D5 business defaults + the DCA-scenario forecast duration +
// the longest-PDP value that powers the too-short warning + the current default
// template name). Read-only; creates nothing. Fired on modal open.
/**
 * Mutation atom that fetches the Aries MDB export config-modal defaults.
 *
 * @param {number} calculationId - The EvaluationCalculation id (route id).
 * @return {Promise<AriesMdbExportDefaults>} The derived default config.
 * @throws {Error} If the request fails (the hook falls back to static defaults).
 */
export const [ariesMdbExportDefaultsBaseAtom] = mutationAtomWithLoadable<
  AriesMdbExportDefaults,
  [number]
>(async (_get, _set, calculationId) => {
  const data = await request.get<AriesMdbExportDefaults>(
    `/evaluations/calculations/${calculationId}/downloads/aries_mdb_export/new`
  );
  if ((data as { error?: string }).error) {
    throw new Error(
      (data as { error: string }).error || 'Failed to load export defaults'
    );
  }
  return data as AriesMdbExportDefaults;
});

// POST /evaluations/calculations/:calculation_id/downloads/aries_mdb_export
// — Kicks off an Aries MDB export (INS-10551). Creates an AriesExportProcess
// (a WorkflowProcess) and returns its Pusher channel + the 14 event
// descriptors (1 overall + 13 steps). INS-10553 supplies the edited config from
// the modal as the request body (camelCase; the BE underscores via rails-boost).
/**
 * Mutation atom that starts an Aries MDB export for a calculation.
 *
 * @param {number} calculationId - The EvaluationCalculation id (route id).
 * @param {AriesMdbExportConfig} config - The edited config from the modal.
 * @return {Promise<AriesMdbKickoffResponse>} Channel + step events to subscribe to.
 * @throws {Error} If the request fails (surfaces the server error string).
 */
export const [ariesMdbExportBaseAtom] = mutationAtomWithLoadable<
  AriesMdbKickoffResponse,
  [number, AriesMdbExportConfig]
>(async (_get, _set, calculationId, config) => {
  const response = await request.post<
    AriesMdbKickoffResponse,
    { error?: string }
  >(
    `/evaluations/calculations/${calculationId}/downloads/aries_mdb_export`,
    config,
    new AbortController()
  );
  if (response.type === 'success') {
    return response.data;
  }
  throw new Error(
    response.type === 'error'
      ? (response.data?.error ?? 'Failed to start Aries MDB export')
      : (response.message ?? 'Failed to start Aries MDB export')
  );
});
